import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  Payment as PaymentIcon,
  MonetizationOn as MoneyIcon,
  CreditCard as CardIcon,
  AccountBalance as DebtIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { 
  recordPostDeliveryPayment, 
  getOutstandingPaymentInfo 
} from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord, MaintenancePaymentMethod } from '../../utils/types';

interface PostDeliveryPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

const PostDeliveryPaymentDialog: React.FC<PostDeliveryPaymentDialogProps> = ({
  open,
  onClose,
  maintenance,
  userId,
  userName,
  onSuccess
}) => {
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<MaintenancePaymentMethod>('cash');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // حساب معلومات الدفع
  const paymentInfo = getOutstandingPaymentInfo(maintenance);

  // تحديث القيم عند فتح النافذة
  useEffect(() => {
    if (open && maintenance) {
      setPaymentAmount(paymentInfo.remainingAmount);
      setPaymentMethod('cash');
      setNotes('');
      setError(null);
    }
  }, [open, maintenance, paymentInfo.remainingAmount]);

  const handleSubmit = async () => {
    if (paymentAmount <= 0) {
      setError('يجب إدخال مبلغ أكبر من صفر');
      return;
    }

    if (paymentAmount > paymentInfo.remainingAmount) {
      setError(`المبلغ لا يمكن أن يتجاوز المبلغ المتبقي: ${formatCurrency(paymentInfo.remainingAmount)}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await recordPostDeliveryPayment(
        maintenance.id,
        paymentAmount,
        paymentMethod,
        userId,
        userName,
        notes || undefined
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error recording post-delivery payment:', error);
      setError('حدث خطأ أثناء تسديد نقدي أو دين');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  const willBeFullyPaid = paymentAmount >= paymentInfo.remainingAmount;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon color="primary" />
          <Typography variant="h6">
            تسديد نقدي أو دين بعد التسليم
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات الطلب */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات الطلب
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>العميل:</strong> {maintenance.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>نوع الجهاز:</strong> {maintenance.deviceType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة النهائية:</strong> {formatCurrency(paymentInfo.finalCost)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>المبلغ المدفوع:</strong> {formatCurrency(paymentInfo.totalPaid)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* حالة الدفع الحالية */}
          <Alert 
            severity={paymentInfo.hasOutstandingPayment ? 'warning' : 'success'} 
            sx={{ mb: 3 }}
            icon={paymentInfo.hasOutstandingPayment ? <WarningIcon /> : <CheckIcon />}
          >
            <Typography variant="body2">
              {paymentInfo.hasOutstandingPayment ? (
                <>
                  <strong>يوجد مبلغ متبقي:</strong> {formatCurrency(paymentInfo.remainingAmount)}
                  {paymentInfo.hasDebt && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      💳 مسجل كدين في النظام
                    </Typography>
                  )}
                </>
              ) : (
                <strong>تم السداد بالكامل</strong>
              )}
            </Typography>
          </Alert>

          {/* إدخال مبلغ الدفعة */}
          <TextField
            label="مبلغ الدفعة"
            type="number"
            fullWidth
            value={paymentAmount || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setPaymentAmount(Math.max(0, Math.min(value, paymentInfo.remainingAmount)));
              setError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
              inputProps: { min: 0, max: paymentInfo.remainingAmount, step: 0.01 }
            }}
            helperText={`الحد الأقصى: ${formatCurrency(paymentInfo.remainingAmount)}`}
            disabled={loading || !paymentInfo.hasOutstandingPayment}
            sx={{ mb: 2 }}
          />

          {/* طريقة الدفع */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>طريقة الدفع</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as MaintenancePaymentMethod)}
              label="طريقة الدفع"
              disabled={loading}
            >
              <MenuItem value="cash">
                <Box display="flex" alignItems="center" gap={1}>
                  <CardIcon fontSize="small" />
                  نقدي
                </Box>
              </MenuItem>
              <MenuItem value="debt">
                <Box display="flex" alignItems="center" gap={1}>
                  <DebtIcon fontSize="small" />
                  خصم من الدين
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* ملاحظات */}
          <TextField
            label="ملاحظات الدفعة"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أضف ملاحظات حول هذه الدفعة..."
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* ملخص الدفعة */}
          {paymentAmount > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.light' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                ملخص الدفعة
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>مبلغ الدفعة:</strong> {formatCurrency(paymentAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>طريقة الدفع:</strong> {paymentMethod === 'cash' ? 'نقدي' : 'خصم من الدين'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>المبلغ بعد الدفع:</strong> {formatCurrency(paymentInfo.remainingAmount - paymentAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>حالة السداد:</strong> {willBeFullyPaid ? 'مكتمل' : 'جزئي'}
                  </Typography>
                </Grid>
              </Grid>
              
              {willBeFullyPaid && (
                <Box mt={2}>
                  <Chip
                    label="✅ سيتم إكمال السداد"
                    color="success"
                    size="small"
                  />
                </Box>
              )}
            </Paper>
          )}

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* معلومات إضافية */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>ملاحظة:</strong> سيتم تحديث حالة الدفع وسجل الديون تلقائياً بعد تسجيل هذه الدفعة.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || paymentAmount <= 0 || !paymentInfo.hasOutstandingPayment}
          startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
        >
          {loading ? 'جاري التسديد...' : 'تسديد نقدي أو دين'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostDeliveryPaymentDialog;
